import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
/** One `share_filter` criterion — a named audience-selector row, not a raw Directus filter. */
export const ShareFilterEntry = S.Struct({
    type: S.String,
    id: S.String,
    name: S.String,
});
/**
 * A `saved_searches` row — the audience-membership record the sharing
 * engine's write endpoints (`assignments.share/items`) maintain. This
 * contract only reads it: admin config screens (e.g. the add-client
 * wizard's contract-obligations step) show the current filter/audience for
 * one `(client_id, item_id, type)` triple, they never write it directly.
 */
export const SavedSearchRow = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
    item_id: S.String,
    collection: S.String,
    type: S.String,
    filter: S.Array(ShareFilterEntry),
    user_ids: S.Array(S.UUID),
    auto_assign_share: S.Boolean,
    created_by: S.NullOr(S.UUID),
    date_created: S.NullOr(S.String),
});
/**
 * `(client_id, item_id, type)` is the natural key one active saved search
 * for an item ever has — see `assignments.share/items`'s own
 * read-before-write lookup on this same triple.
 */
export const GetSavedSearchParams = S.Struct({
    client_id: S.UUID,
    item_id: S.String,
    type: S.Literal("share", "assignment"),
});
export const GetSavedSearch = HttpApiEndpoint.get("getSavedSearch", "/admin/saved-searches/get")
    .setUrlParams(GetSavedSearchParams)
    .addSuccess(S.Struct({ data: S.NullOr(SavedSearchRow) }))
    .addError(Forbidden, { status: 403 });
/**
 * Same `(client_id, item_id, type)` read as `getSavedSearch`, scoped to the
 * caller's own client instead of admin/external-sifa — the dashboard
 * `AssetShareModal`'s own init read (existing audience + `auto_assign_share`
 * for one item) needs the same row `assignments.share/items`'s handlers
 * already let an ohs-officer/location-manager write.
 */
export const GetShareAudience = HttpApiEndpoint.get("getShareAudience", "/dashboard/sharing/audience")
    .setUrlParams(GetSavedSearchParams)
    .addSuccess(S.Struct({ data: S.NullOr(SavedSearchRow) }))
    .addError(Forbidden, { status: 403 });
/**
 * The same `(client_id, item_id, type)` lookup as `getSavedSearch`/
 * `getShareAudience`, list-shaped for the screens that still call the
 * generic `itemsApi.list` on this table directly (the share-folder-with-
 * employees lookup, the measure-assign audience read) — the `unique_saved_search`
 * constraint on `(item_id, client_id, type)` means this is always 0 or 1 row,
 * never a genuine multi-row page. Authorized the same broad way
 * `getShareAudience` is (admin/ohs-admin/sifa/ama anywhere, ohs-officer/
 * location-manager their own client) so either persona's screen can call it.
 */
export const ListSavedSearchesByFilter = HttpApiEndpoint.get("listSavedSearchesByFilter", "/saved-searches/list")
    .setUrlParams(GetSavedSearchParams)
    .addSuccess(S.Struct({ data: S.Array(SavedSearchRow) }))
    .addError(Forbidden, { status: 403 });
/** A saved search already exists for this `(client_id, item_id, type)` triple. */
export const SavedSearchExists = S.Struct({
    _tag: S.Literal("SavedSearchExists"),
});
export const CreateSavedSearchPayload = S.Struct({
    client_id: S.UUID,
    item_id: S.String,
    collection: S.Literal("yp_files", "online_courses", "measures", "measure_templates", "clients", "yp_folders"),
    type: S.Literal("share", "assignment"),
    filter: S.Array(ShareFilterEntry),
    user_ids: S.Array(S.UUID),
    auto_assign_share: S.Boolean,
});
/**
 * A direct insert for callers that don't go through the sharing engine's
 * own upsert (`assignments.share/items`) — the measure-assign audience and
 * the share-folder lookup manage the row themselves rather than through a
 * shared item kind. `created_by` is always the caller, never client-supplied.
 */
export const CreateSavedSearch = HttpApiEndpoint.post("createSavedSearch", "/saved-searches/create")
    .setPayload(CreateSavedSearchPayload)
    .addSuccess(S.Struct({ data: SavedSearchRow }))
    .addError(SavedSearchExists, { status: 409 })
    .addError(Forbidden, { status: 403 });
/**
 * The `id` on `deleteSavedSearch` doesn't exist (or isn't in a client the
 * caller can act on) — structurally identical to (but a separate
 * declaration from) `sharing.ts`'s own `SavedSearchNotFound`, since that
 * module already imports `ShareFilterEntry` from this one and importing
 * back would cycle the two.
 */
export const SavedSearchNotFound = S.Struct({
    _tag: S.Literal("SavedSearchNotFound"),
});
export const DeleteSavedSearchPayload = S.Struct({ id: S.UUID });
export const DeleteSavedSearch = HttpApiEndpoint.post("deleteSavedSearch", "/saved-searches/delete")
    .setPayload(DeleteSavedSearchPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(SavedSearchNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const savedSearchesGroup = HttpApiGroup.make("saved-searches")
    .add(GetSavedSearch)
    .add(GetShareAudience)
    .add(ListSavedSearchesByFilter)
    .add(CreateSavedSearch)
    .add(DeleteSavedSearch)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=saved-searches.js.map